import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DatabaseBackupIcon, DownloadIcon, HistoryIcon, UploadIcon } from 'lucide-react'
import { toast } from 'sonner'
import { formatDecimalHours, type BackupSummary } from '@coneno/c-sheet-shared'
import { PageBody, PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { downloadFromServer } from '@/lib/download'
import { useAppConfig } from '@/hooks/use-session'
import { useTRPC } from '@/lib/trpc'

type Preview = { source: 'web' | 'desktop'; summary: BackupSummary; current: BackupSummary }

class UploadError extends Error {
  constructor(readonly problems: string[]) {
    super(problems[0] ?? 'The file could not be read.')
  }
}

async function upload<T>(path: string, file: File, passcode?: string): Promise<T> {
  const form = new FormData()
  form.set('file', file)
  if (passcode !== undefined) form.set('passcode', passcode)
  const response = await fetch(path, { method: 'POST', body: form })
  const body = (await response.json().catch(() => ({}))) as {
    problems?: string[]
    message?: string
  }
  if (!response.ok) {
    throw new UploadError(body.problems ?? [body.message ?? 'The file could not be uploaded.'])
  }
  return body as T
}

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short'
})

function formatRange(summary: BackupSummary): string {
  if (!summary.firstWorkDate || !summary.lastWorkDate) return '–'
  const first = dateFormat.format(new Date(`${summary.firstWorkDate}T12:00:00`))
  const last = dateFormat.format(new Date(`${summary.lastWorkDate}T12:00:00`))
  return first === last ? first : `${first} – ${last}`
}

function SummaryTable({ file, current }: { file: BackupSummary; current: BackupSummary }) {
  const rows: [string, (summary: BackupSummary) => string][] = [
    ['Time entries', (summary) => String(summary.timeEntries)],
    ['Hours', (summary) => `${formatDecimalHours(summary.totalMinutes)} h`],
    ['Dates', formatRange],
    ['People', (summary) => String(summary.people)],
    ['Clients', (summary) => String(summary.customers)],
    ['Projects', (summary) => String(summary.projects)],
    ['Tasks', (summary) => String(summary.tasks)]
  ]
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-xs text-muted-foreground">
          <th className="py-2 font-medium" />
          <th className="py-2 font-medium">In the file</th>
          <th className="py-2 font-medium">In c-sheet now</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label} className="border-b last:border-b-0">
            <th className="py-2 pr-4 text-left font-normal text-muted-foreground">{label}</th>
            <td className="py-2 pr-4 font-medium tabular-nums">{value(file)}</td>
            <td className="py-2 tabular-nums">{value(current)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function PasscodeField({
  value,
  onChange,
  disabled
}: {
  value: string
  onChange(value: string): void
  disabled: boolean
}): React.JSX.Element {
  return (
    <Field>
      <FieldLabel htmlFor="restore-passcode">Restore passcode</FieldLabel>
      <Input
        id="restore-passcode"
        type="password"
        value={value}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      <FieldDescription>Set on the server as RESTORE_PASSCODE.</FieldDescription>
    </Field>
  )
}

export function DataSettingsPage(): React.JSX.Element {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [passcode, setPasscode] = useState('')
  const [undoSnapshotId, setUndoSnapshotId] = useState<string | null>(null)
  const snapshots = useQuery(trpc.backup.snapshots.queryOptions())
  const restoreEnabled = useAppConfig().data?.restoreEnabled ?? true

  const preview = useMutation({
    mutationFn: (selected: File) => upload<Preview>('/api/restore/preview', selected)
  })
  const restore = useMutation({
    mutationFn: (selected: File) =>
      upload<{ summary: BackupSummary }>('/api/restore', selected, passcode),
    onSuccess: async ({ summary }) => {
      setConfirmOpen(false)
      setFile(null)
      preview.reset()
      if (fileInput.current) fileInput.current.value = ''
      await queryClient.invalidateQueries()
      toast(
        `Restored ${summary.timeEntries} entries. The previous data is kept under Restore history.`
      )
    }
  })
  const undo = useMutation(
    trpc.backup.restoreSnapshot.mutationOptions({
      onSuccess: async () => {
        setUndoSnapshotId(null)
        await queryClient.invalidateQueries()
        toast('Restore undone')
      }
    })
  )

  function chooseFile(selected: File | null): void {
    setFile(selected)
    restore.reset()
    if (selected) preview.mutate(selected)
    else preview.reset()
  }

  const problems =
    preview.error instanceof UploadError
      ? preview.error.problems
      : preview.error
        ? [preview.error.message]
        : []

  return (
    <PageBody width="narrow">
      <PageHeader
        title="Data & backup"
        description="Download a backup of all timesheet data, or restore c-sheet from one."
      />

      <Card>
        <CardHeader>
          <CardTitle>Download backup</CardTitle>
          <CardDescription>
            A JSON file with every person, client, project, task and time entry. Sign-in accounts
            are not included.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => downloadFromServer('/api/backup')}>
            <DownloadIcon data-icon="inline-start" />
            Download backup
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Restore from a file</CardTitle>
          <CardDescription>
            Accepts a c-sheet backup (.json) or a backup made by the c-sheet desktop app (.sqlite).
            Restoring replaces all current data. The data you have now is kept under Restore history
            first.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Field>
            <FieldLabel htmlFor="restore-file">Backup file</FieldLabel>
            <Input
              id="restore-file"
              ref={fileInput}
              type="file"
              accept=".json,.sqlite,application/json"
              onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
              disabled={restore.isPending || !restoreEnabled}
            />
            <FieldDescription>
              {restoreEnabled
                ? 'Nothing changes until you confirm with the restore passcode.'
                : 'Restoring is turned off on this server. Set RESTORE_PASSCODE to turn it on.'}
            </FieldDescription>
          </Field>

          {preview.isPending && <p className="text-sm text-muted-foreground">Checking the file…</p>}
          {problems.length > 0 && (
            <div role="alert" className="rounded-md border border-destructive/40 p-3 text-sm">
              <p className="font-medium text-destructive">This file can’t be restored:</p>
              <ul className="mt-1 list-disc pl-5 text-destructive">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}
          {preview.data && file && (
            <>
              <p className="text-sm">
                {preview.data.source === 'desktop' ? 'Desktop app backup' : 'c-sheet backup'}{' '}
                <span className="text-muted-foreground">· {file.name}</span>
              </p>
              <SummaryTable file={preview.data.summary} current={preview.data.current} />
              <Button
                variant="destructive"
                className="self-start"
                onClick={() => {
                  setPasscode('')
                  restore.reset()
                  setConfirmOpen(true)
                }}
              >
                <UploadIcon data-icon="inline-start" />
                Replace all data with this file
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Restore history</CardTitle>
          <CardDescription>
            Before each restore, c-sheet keeps a copy of the data it replaced. The last five are
            kept.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snapshots.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : snapshots.error ? (
            <p className="text-sm text-destructive">{snapshots.error.message}</p>
          ) : snapshots.data.length ? (
            <ul className="flex flex-col divide-y">
              {snapshots.data.map((snapshot) => (
                <li key={snapshot.id} className="flex flex-wrap items-center gap-3 py-3 text-sm">
                  <HistoryIcon className="size-4 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{snapshot.reason}</p>
                    <p className="text-muted-foreground">
                      {dateTimeFormat.format(new Date(snapshot.createdAt))} ·{' '}
                      {snapshot.summary.timeEntries} entries ·{' '}
                      {formatDecimalHours(snapshot.summary.totalMinutes)} h
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => downloadFromServer(`/api/backup/snapshots/${snapshot.id}`)}
                  >
                    <DownloadIcon data-icon="inline-start" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!restoreEnabled}
                    onClick={() => {
                      setPasscode('')
                      undo.reset()
                      setUndoSnapshotId(snapshot.id)
                    }}
                  >
                    Restore this copy
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <DatabaseBackupIcon className="size-4" aria-hidden="true" />
              No restores yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!restore.isPending) setConfirmOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace all data?</DialogTitle>
            <DialogDescription>
              {preview.data
                ? `All ${preview.data.current.timeEntries} current entries will be replaced by the ${preview.data.summary.timeEntries} entries in ${file?.name}. The current data is kept under Restore history.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <PasscodeField value={passcode} onChange={setPasscode} disabled={restore.isPending} />
          {restore.error && (
            <p role="alert" className="text-sm text-destructive">
              {restore.error.message}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={restore.isPending}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!file || !passcode || restore.isPending}
              onClick={() => file && restore.mutate(file)}
            >
              {restore.isPending ? 'Restoring…' : 'Replace all data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={undoSnapshotId !== null}
        onOpenChange={(open) => {
          if (!open && !undo.isPending) setUndoSnapshotId(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore this copy?</DialogTitle>
            <DialogDescription>
              All current data is replaced by this copy. The current data is kept under Restore
              history first, so this can be undone as well.
            </DialogDescription>
          </DialogHeader>
          <PasscodeField value={passcode} onChange={setPasscode} disabled={undo.isPending} />
          {undo.error && (
            <p role="alert" className="text-sm text-destructive">
              {undo.error.message}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={undo.isPending}
              onClick={() => setUndoSnapshotId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!passcode || undo.isPending}
              onClick={() => undoSnapshotId && undo.mutate({ id: undoSnapshotId, passcode })}
            >
              {undo.isPending ? 'Restoring…' : 'Restore this copy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageBody>
  )
}
